import { CloseOutlined, DownloadOutlined, EmojiEventsOutlined, ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Button,
  Drawer,
  Fab,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useRepCodes } from 'src/hooks'
import { formatCurrency } from 'src/helper'
import { isLastChildInBundle, toFlatRenderItems } from '../../lines/lineTableUtils'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import { getQuoteCrm } from '../../quoteCrmCache'
import SupplierQuotesSidebar from '../../lines/SupplierQuotesSidebar'
import { downloadQuotePdf } from '../export/QuotePdfDocument'
import {
  AddressDetails,
  EMPTY_ADDRESS,
  aeFromRepCode,
  isrFromRepCode,
  shipToFromCrm,
  soldToFromCrm,
} from '../export/quotePdfModel'
import '../../lines/LinesShared.css'
import './SentToCustomer.css'

const COLUMN_WIDTHS = {
  NUMBER: '80px',
  MFR_PART_NUMBER: '150px',
  DESCRIPTION: '350px',
  DISTRIBUTOR: '130px',
  MANUFACTURER: '110px',
  TAA: '120px',
  CONTRACT_PRICE: '130px',
  CONTRACT_CATALOG: '130px',
  QTY: '80px',
  UNIT_COST: '120px',
  UNIT_PRICE: '130px',
  MARGIN: '100px',
}

type SentToCustomerProps = {
  onClosed: (status: 'Closed Won' | 'Closed Lost') => void
}

export const SentToCustomer = ({ onClosed }: SentToCustomerProps) => {
  const {
    quote,
    lineItems,
    lineItemsLoading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    collapsedBundles,
    setCollapsedBundles,
    lineCount,
    sortedLineItems,
    quoteId,
    vendorQuotes,
  } = useQuoteLines()
  const [repCodes] = useRepCodes()
  const flatRenderItems = useMemo(() => toFlatRenderItems(sortedLineItems), [sortedLineItems])
  const [closedMenuOpen, setClosedMenuOpen] = useState(false)
  const [supplierQuotesOpen, setSupplierQuotesOpen] = useState(false)
  const [soldToDetails, setSoldToDetails] = useState<AddressDetails>({ ...EMPTY_ADDRESS })
  const [shippingDetails, setShippingDetails] = useState<AddressDetails>({ ...EMPTY_ADDRESS })
  const rep = repCodes.find(r => r.RepCode === quote.RepCode) ?? null
  const isrDetails = isrFromRepCode(rep)
  const aeDetails = aeFromRepCode(rep)

  useEffect(() => {
    if (!quoteId) return
    getQuoteCrm(quoteId)
      .then(({ account, contact }) => {
        setSoldToDetails(soldToFromCrm(account, contact))
        setShippingDetails(shipToFromCrm(account, contact))
      })
      .catch(err => console.error(err))
  }, [quoteId])

  return (
    <div className="lines-view sent-to-customer">
      <div className="lines-header-buttons">
        <div className="lines-header-buttons-left">
          <Button
            variant="outlined"
            startIcon={<DownloadOutlined />}
            onClick={() =>
              downloadQuotePdf({ quote, lineItems, soldToDetails, shippingDetails, isrDetails, aeDetails }).catch(err =>
                console.error(err),
              )
            }
          >
            Download Quote
          </Button>
          <Button variant="outlined" onClick={() => setSupplierQuotesOpen(true)}>
            Supplier Quotes
          </Button>
        </div>
        <div className="lines-header-buttons-right">
          <TablePagination
            count={lineCount}
            onPageChange={(_, newPage) => {
              setPage(newPage)
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value))
              setPage(0)
            }}
            component="div"
          />
        </div>
      </div>
      <TableContainer className="lines-table-container">
        <Table
          stickyHeader
          sx={{ minWidth: `${Object.values(COLUMN_WIDTHS).reduce((acc, curr) => acc + parseInt(curr), 0)}px` }}
        >
          <TableHead>
            <TableRow>
              <TableCell className="lines-sticky-col" width={COLUMN_WIDTHS.NUMBER}>
                <div className="lines-sticky-col-content">
                  #
                  <IconButton
                    size="small"
                    onClick={() =>
                      setCollapsedBundles(prev => {
                        const newSet = new Set(prev)
                        newSet.size === lineItems.length
                          ? newSet.clear()
                          : lineItems.forEach(item => newSet.add(item.Bundle ?? 'no-bundle'))
                        return newSet
                      })
                    }
                  >
                    {collapsedBundles.size === lineItems.length ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </div>
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.MFR_PART_NUMBER}>Mfr Part #</TableCell>
              <TableCell sx={{ minWidth: COLUMN_WIDTHS.DESCRIPTION }}>Description</TableCell>
              <TableCell width={COLUMN_WIDTHS.DISTRIBUTOR}>Supplier</TableCell>
              <TableCell width={COLUMN_WIDTHS.MANUFACTURER}>Manufacturer</TableCell>
              <TableCell width={COLUMN_WIDTHS.TAA}>TAA Compliant</TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.CONTRACT_PRICE}>
                Contract Ceiling Price
              </TableCell>
              <TableCell width={COLUMN_WIDTHS.CONTRACT_CATALOG}>Contract Catalog</TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.QTY}>
                Qty
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.UNIT_COST}>
                Unit Cost
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.UNIT_PRICE}>
                Unit Price
              </TableCell>
              <TableCell align="right" width={COLUMN_WIDTHS.MARGIN}>
                GP %
              </TableCell>
            </TableRow>
          </TableHead>
          {lineItemsLoading ? (
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 12 }).map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              {flatRenderItems.map(renderItem => {
                const lastChild = isLastChildInBundle(lineItems, renderItem)

                if (renderItem.isHeader) {
                  return (
                    <TableRow key={renderItem.item.LineItemId} className="bundle-row">
                      <TableCell className="lines-sticky-col">
                        <div className="lines-sticky-col-content">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setCollapsedBundles(prev => {
                                const newSet = new Set(prev)
                                newSet.has(renderItem.item.Bundle ?? 'no-bundle')
                                  ? newSet.delete(renderItem.item.Bundle ?? 'no-bundle')
                                  : newSet.add(renderItem.item.Bundle ?? 'no-bundle')
                                return newSet
                              })
                            }
                          >
                            {collapsedBundles.has(renderItem.item.Bundle ?? 'no-bundle') ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </IconButton>
                        </div>
                      </TableCell>
                      <TableCell className="bundle-small-text">
                        {renderItem.item.MfrPartNumber ?? 'No Bundle'}
                      </TableCell>
                      <TableCell className="lines-ellipsis-col">{renderItem.item.Description}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>{renderItem.item.TAA == null ? '-' : renderItem.item.TAA ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {formatCurrency(renderItem.item.ContractCeilingPrice, '-')}
                      </TableCell>
                      <TableCell />
                      <TableCell align="right" className="bundle-small-text">
                        {renderItem.item.Qty ?? '-'}
                      </TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {formatCurrency(renderItem.item.UnitCost, '-')}
                      </TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {formatCurrency(renderItem.item.UnitPrice, '-')}
                      </TableCell>
                      <TableCell align="right" className="bundle-small-text">
                        {renderItem.item.Margin != null ? `${(renderItem.item.Margin * 100).toFixed(2)}%` : '-'}
                      </TableCell>
                    </TableRow>
                  )
                }

                return (
                  <TableRow
                    key={renderItem.item.LineItemId}
                    className={renderItem.bundleId === null ? 'no-bundle' : lastChild ? 'child-row-last' : undefined}
                    sx={{
                      display: collapsedBundles.has(renderItem.bundleId ?? 'no-bundle') ? 'none' : 'table-row',
                    }}
                  >
                    <TableCell className="lines-sticky-col">
                      <Typography variant="body2">
                        {renderItem.item.LineNumber !== -1 ? renderItem.item.LineNumber : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderItem.item.MfrPartNumber ?? '-'}</TableCell>
                    <TableCell className="lines-ellipsis-col">{renderItem.item.Description ?? '-'}</TableCell>
                    <TableCell>{renderItem.item.Provider ?? '-'}</TableCell>
                    <TableCell className="lines-ellipsis-col">{renderItem.item.Manufacturer ?? '-'}</TableCell>
                    <TableCell>{renderItem.item.TAA == null ? '-' : renderItem.item.TAA ? 'Yes' : 'No'}</TableCell>
                    <TableCell align="right">{formatCurrency(renderItem.item.ContractCeilingPrice, '-')}</TableCell>
                    <TableCell>{renderItem.item.ContractCatalog ?? '-'}</TableCell>
                    <TableCell align="right">{renderItem.item.Qty ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(renderItem.item.UnitCost, '-')}</TableCell>
                    <TableCell align="right">{formatCurrency(renderItem.item.UnitPrice, '-')}</TableCell>
                    <TableCell align="right">
                      {renderItem.item.Margin != null ? `${(renderItem.item.Margin * 100).toFixed(2)}%` : '-'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <Drawer
        className="supplier-quotes-drawer"
        open={supplierQuotesOpen}
        onClose={() => setSupplierQuotesOpen(false)}
        anchor="right"
      >
        <SupplierQuotesSidebar
          onClose={() => setSupplierQuotesOpen(false)}
          quoteId={quoteId ?? ''}
          vendorQuotes={vendorQuotes}
          lineItems={lineItems}
          viewOnly
        />
      </Drawer>
      <div
        className={`closed-actions${closedMenuOpen ? ' open' : ''}`}
        onMouseEnter={() => setClosedMenuOpen(true)}
        onMouseLeave={() => setClosedMenuOpen(false)}
      >
        <div className="closed-menu">
          <Fab size="large" variant="extended" onClick={() => onClosed('Closed Won')}>
            Won <EmojiEventsOutlined fontSize="small" />
          </Fab>
          <Fab size="large" variant="extended" onClick={() => onClosed('Closed Lost')}>
            Lost <CloseOutlined fontSize="small" />
          </Fab>
        </div>
        <Fab className="closed-fab" color="primary" size="large" variant="extended">
          Closed Won/Lost
        </Fab>
      </div>
    </div>
  )
}
